import { Component, OnDestroy } from '@angular/core'
import { Store } from '@ngrx/store'
import {
    selectCaseHasSavedFreeSelections,
    selectFreeSelectionName,
    selectSavedFreeSelectionNamesBySegSource,
} from '../../../state/selection/selection.selectors'
import { selectionActions } from '../../../state/selection/selection.actions'
import { Dialog } from '@angular/cdk/dialog'
import { SaveSelectionAsDialogComponent } from '../../save-selection-as-dialog/save-selection-as-dialog.component'
import { Subject, takeUntil } from 'rxjs'
import { GenericConfirmationDialogComponent } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component'
import { GenericConfirmationDialogModel } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.model'
import { DownloadService } from '../../../shared/services/download.service'
import { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from '../../../shared/services/toaster.service'
import { selectIsSharedCase } from '../../../state/case/case.selectors'
import { environment } from '../../../../environments/environment'

@Component({
    selector: 'app-saved-free-selections',
    templateUrl: './saved-free-selections.component.html',
    styleUrls: ['./saved-free-selections.component.scss'],
})
export class SavedFreeSelectionsComponent implements OnDestroy {
    readonly hasSavedSelections$ = this.store.select(selectCaseHasSavedFreeSelections)
    readonly selectionName$ = this.store.select(selectFreeSelectionName)
    readonly selectionNames$ = this.store.select(selectSavedFreeSelectionNamesBySegSource)
    readonly isSharedCase$ = this.store.select(selectIsSharedCase)

    private readonly destroy$ = new Subject<void>()
    private readonly wikiUrl = environment.wikiUrl

    constructor(
        private readonly store: Store,
        private readonly dialog: Dialog,
        private readonly toaster: ToasterService,
        private readonly downloader: DownloadService
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    saveSelectionAs(): void {
        const dialogRef = this.dialog.open<string>(SaveSelectionAsDialogComponent, {
            autoFocus: 'first-tabbable',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(name => {
            if (!name) return
            this.store.dispatch(selectionActions.saveNewFreeSelection({ name }))
        })
    }

    saveSelection(): void {
        this.store.dispatch(selectionActions.saveFreeSelection())
    }

    downloadSelections(): void {
        this.downloader.downloadSelections().subscribe({
            error: (err: HttpErrorResponse) => {
                this.toaster.httpError(err, 'Failed to download selections.')
            },
        })
    }

    setSelection(name: string | undefined): void {
        this.store.dispatch(selectionActions.setFreeSelection({ name }))
    }

    deleteSelection(selectionName: string | undefined): void {
        const data: GenericConfirmationDialogModel = {
            title: 'Are you sure you want to delete this selection?',
            item: selectionName,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }
        const dialogRef = this.dialog.open<string>(GenericConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            this.store.dispatch(selectionActions.deleteFreeSelection())
        })
    }

    goToWiki(): void {
        window.open(`${this.wikiUrl}home/Navigation#gating-and-statistical-analysis`, '_blank')
    }
}
